import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { SearchFieldComponent } from '@app/v2/shared/components/search-field/search-field.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { BlacklistEntry, BlacklistPagination, CreateBlacklistPayload } from '../../models/blacklist.model'
import { BlacklistService } from '../../services/blacklist.service'

const PER_PAGE = 20
const IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/

@Component({
  selector: 'app-blacklist',
  standalone: true,
  imports: [ReactiveFormsModule, SearchFieldComponent, NgClass],
  providers: [BlacklistService],
  templateUrl: './blacklist.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BlacklistComponent implements OnInit {
  public readonly items = signal<BlacklistEntry[]>([])
  public readonly pagination = signal<BlacklistPagination | null>(null)
  public readonly isLoading = signal(false)
  public readonly isSaving = signal(false)
  public readonly isFormOpen = signal(false)
  public readonly page = signal(1)
  public readonly pageNumbers = computed(() => this._buildPageNumbers(this.pagination()?.current_page ?? this.page(), this.pagination()?.last_page ?? 1))

  public readonly form = inject(FormBuilder).group(
    {
      email: ['', Validators.email],
      ip_address: ['', this._ipValidator],
      full_name: [''],
      phone_number: [''],
      country: ['', Validators.pattern(/^[A-Za-z]{2}$/)]
    },
    { validators: this._emailOrIpRequired }
  )

  private readonly _search = signal('')
  private readonly _blacklistService = inject(BlacklistService)
  private readonly _alertService = inject(AlertService)
  private readonly _destroyRef = inject(DestroyRef)
  private _loadId = 0

  public ngOnInit(): void {
    this.load()
  }

  public onSearch(term: string): void {
    this._search.set(term)
    this.page.set(1)
    this.load()
  }

  public onAdd(): void {
    this.form.reset({ email: '', ip_address: '', full_name: '', phone_number: '', country: '' })
    this.isFormOpen.set(true)
  }

  public onCancel(): void {
    this.isFormOpen.set(false)
    this.form.reset({ email: '', ip_address: '', full_name: '', phone_number: '', country: '' })
  }

  public onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }

    this.isSaving.set(true)
    this._blacklistService
      .add(this._payload())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          this._alertService.alert(Alert.SUCCESS, result.message || 'User added to blacklist successfully')
          this.isSaving.set(false)
          this.onCancel()
          this.page.set(1)
          this.load()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message || 'Failed to add to blacklist')
          this.isSaving.set(false)
        }
      })
  }

  public load(): void {
    const loadId = ++this._loadId
    this.isLoading.set(true)
    this._blacklistService
      .list({ search: this._search(), page: this.page(), per_page: PER_PAGE })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          if (loadId !== this._loadId) return
          this.items.set(result.items)
          this.pagination.set(result.pagination)
          this.page.set(result.pagination.current_page)
          this.isLoading.set(false)
        },
        error: (error) => {
          if (loadId !== this._loadId) return
          this._alertService.alert(Alert.ERROR, error?.error?.message || 'Failed to load blacklist')
          this.items.set([])
          this.pagination.set(null)
          this.isLoading.set(false)
        }
      })
  }

  public goToPage(page: number): void {
    const lastPage = this.pagination()?.last_page ?? 1
    if (page < 1 || page > lastPage || page === this.page() || this.isLoading()) return
    this.page.set(page)
    this.load()
  }

  public rangeStart(): number {
    const pager = this.pagination()
    if (!pager || pager.total === 0) return 0
    return (pager.current_page - 1) * pager.per_page + 1
  }

  public rangeEnd(): number {
    const pager = this.pagination()
    if (!pager) return 0
    return Math.min(pager.current_page * pager.per_page, pager.total)
  }

  public display(value: string | null | undefined): string {
    const trimmed = value?.trim()
    return trimmed ? trimmed : '—'
  }

  private _payload(): CreateBlacklistPayload {
    const raw = this.form.getRawValue()
    const payload: CreateBlacklistPayload = {}
    const email = raw.email?.trim()
    const ipAddress = raw.ip_address?.trim()
    const fullName = raw.full_name?.trim()
    const phoneNumber = raw.phone_number?.trim()
    const country = raw.country?.trim().toUpperCase()

    if (email) payload.email = email
    if (ipAddress) payload.ip_address = ipAddress
    if (fullName) payload.full_name = fullName
    if (phoneNumber) payload.phone_number = phoneNumber
    if (country) payload.country = country

    return payload
  }

  private _buildPageNumbers(current: number, last: number): Array<number | 'ellipsis'> {
    if (last <= 1) return [1]
    if (last <= 5) return Array.from({ length: last }, (_, index) => index + 1)

    const pages = new Set<number>([1, last, current])
    if (current > 1) pages.add(current - 1)
    if (current < last) pages.add(current + 1)

    const sorted = Array.from(pages).sort((a, b) => a - b)
    const result: Array<number | 'ellipsis'> = []

    sorted.forEach((page, index) => {
      const previous = sorted[index - 1]
      if (previous != null && page - previous > 1) result.push('ellipsis')
      result.push(page)
    })

    return result
  }

  private _emailOrIpRequired(group: AbstractControl): ValidationErrors | null {
    const email = group.get('email')?.value?.trim()
    const ipAddress = group.get('ip_address')?.value?.trim()
    return email || ipAddress ? null : { emailOrIpRequired: true }
  }

  private _ipValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim()
    if (!value) return null
    if (IPV4.test(value)) return null
    if (value.includes(':') && /^[0-9a-fA-F:]+$/.test(value)) return null
    return { ip: true }
  }
}
